/**
 * O histórico da conversa, com abertura progressiva (H04, §11.2).
 *
 * ── Duas fontes, e por que não uma consulta infinita ──────────────────────
 *
 * A §11.2 pede abertura progressiva: as últimas N mensagens, com carregamento
 * acima ao rolar. Conversa de seis meses não pode carregar inteira.
 *
 * O caminho óbvio seria `useInfiniteQuery`, que acumula páginas sozinho. Ele
 * tem um problema aqui: quando o SSE avisa que a conversa mudou, ele rebusca
 * TODAS as páginas carregadas. Numa conversa com dez páginas abertas, cada
 * mensagem nova custaria dez requisições — e mensagem nova é o evento mais
 * frequente desta tela.
 *
 * Então são duas fontes, com tempos de vida diferentes:
 *
 *   **A página de cima** é uma consulta comum, invalidada pelo SSE. É a que
 *   tem a mensagem nova, o status de entrega que muda, o `stale`.
 *
 *   **As páginas antigas** ficam em estado do componente e NUNCA são
 *   rebuscadas. Isso é seguro por uma propriedade real do domínio, não por
 *   economia: mensagem antiga não muda. Ela não é editada, não é apagada, e o
 *   status de entrega dela já estabilizou. O que muda é sempre no fim.
 *
 * O preço é que uma correção retroativa — se um dia existir — não apareceria
 * sem trocar de conversa. Vale o preço: hoje isso não existe, e o custo do
 * outro caminho é dez requisições por mensagem recebida.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ConversaComposta } from '@cliqcrm/contracts';
import { useApi } from '@/shared/api/consultas';
import { useConversa } from '@/shared/api/consultas';

type Mensagem = ConversaComposta['mensagens']['itens'][number];

export interface Historico {
  readonly conversa: ConversaComposta | undefined;
  readonly erro: unknown;
  readonly carregando: boolean;
  /** Antigas primeiro, mais recentes por último — a ordem em que se lê. */
  readonly mensagens: readonly Mensagem[];
  readonly temMaisAntigas: boolean;
  readonly carregandoAntigas: boolean;
  readonly erroAoCarregarAntigas: unknown;
  readonly carregarMaisAntigas: () => void;
  readonly recarregar: () => void;
}

export function useHistorico(conversaId: string | null): Historico {
  const api = useApi();
  const cabeca = useConversa(conversaId);

  const [antigas, definirAntigas] = useState<readonly Mensagem[]>([]);
  const [cursor, definirCursor] = useState<string | null>(null);
  const [temMais, definirTemMais] = useState(false);
  const [buscando, definirBuscando] = useState(false);
  const [erroAntigas, definirErroAntigas] = useState<unknown>(null);

  /**
   * Trocar de conversa zera tudo, e zera ANTES de a nova responder.
   *
   * Sem isto, as mensagens antigas da conversa anterior ficariam na tela
   * enquanto a nova carrega — e por um instante o histórico mostraria duas
   * conversas misturadas. Numa tela de atendimento isso é o pior tipo de
   * defeito: o vendedor responde no contexto errado.
   */
  useEffect(() => {
    definirAntigas([]);
    definirCursor(null);
    definirTemMais(false);
    definirErroAntigas(null);
  }, [conversaId]);

  // O cursor e o "tem mais" saem da página de cima enquanto nada foi carregado
  // acima; depois disso, quem manda é o resultado da última busca de antigas.
  const cursorDaCabeca = cabeca.data?.mensagens.pagina.cursor_anterior ?? null;
  const temMaisNaCabeca = cabeca.data?.mensagens.pagina.tem_mais_antigas ?? false;

  const cursorAtual = cursor ?? cursorDaCabeca;
  const temMaisAntigas = cursor === null ? temMaisNaCabeca : temMais;

  /**
   * Uma busca por vez.
   *
   * O gatilho é rolagem, e rolagem dispara muitas vezes seguidas. Sem a trava,
   * chegar ao topo da lista renderia meia dúzia de requisições com o mesmo
   * cursor, e as respostas iguais seriam concatenadas — o histórico apareceria
   * repetido.
   */
  const emCurso = useRef(false);

  const carregarMaisAntigas = useCallback(() => {
    if (!api || !conversaId || !cursorAtual || emCurso.current) return;

    emCurso.current = true;
    definirBuscando(true);
    definirErroAntigas(null);

    void api
      .comporConversa({ caminho: { id: conversaId }, consulta: { cursor: cursorAtual } })
      .then((pagina) => {
        // Invertida aqui também, pela mesma razão: a página vem em ordem
        // decrescente, e o que entra ACIMA precisa estar crescente.
        const emOrdemDeLeitura = [...pagina.mensagens.itens].reverse();
        definirAntigas((atuais) => [...emOrdemDeLeitura, ...atuais]);
        definirCursor(pagina.mensagens.pagina.cursor_anterior);
        definirTemMais(pagina.mensagens.pagina.tem_mais_antigas);
      })
      .catch((erro: unknown) => definirErroAntigas(erro))
      .finally(() => {
        emCurso.current = false;
        definirBuscando(false);
      });
  }, [api, conversaId, cursorAtual]);

  /**
   * Junta as duas fontes, sem duplicar, e na ordem de LEITURA.
   *
   * A API pagina das mais novas para as mais antigas — é o certo para keyset:
   * a primeira página é a que importa, e o cursor caminha para trás. Mas o
   * histórico se lê de cima para baixo, então cada página é invertida aqui.
   *
   * Sem esta inversão a conversa aparece de cabeça para baixo, com a resposta
   * antes da pergunta. Foi assim que apareceu: contra o banco de verdade, a
   * primeira mensagem da tela era a última do dia.
   *
   * A fronteira entre a página de cima e a primeira antiga pode se sobrepor: se
   * chegou mensagem entre uma busca e outra, o cursor desloca e o mesmo item
   * cai nas duas. A referência externa é a identidade da mensagem (a projeção
   * não cunha id por mensagem — decisão da C19), então é por ela que se
   * deduplica.
   */
  const mensagens = useMemo(() => {
    const daCabeca = [...(cabeca.data?.mensagens.itens ?? [])].reverse();
    const vistas = new Set<string>();
    const juntas: Mensagem[] = [];

    for (const mensagem of [...antigas, ...daCabeca]) {
      if (vistas.has(mensagem.referencia_externa)) continue;
      vistas.add(mensagem.referencia_externa);
      juntas.push(mensagem);
    }

    return juntas;
  }, [antigas, cabeca.data]);

  return {
    conversa: cabeca.data,
    erro: cabeca.error,
    carregando: cabeca.isPending,
    mensagens,
    temMaisAntigas,
    carregandoAntigas: buscando,
    erroAoCarregarAntigas: erroAntigas,
    carregarMaisAntigas,
    recarregar: () => void cabeca.refetch(),
  };
}
